import { computed, ref } from 'vue';
import { logWarn } from '@/common/helpers';
import PlayAccountProvider, { PLAY_ACCOUNT_ID } from '@/composables/accounts/providers/playAccountProvider.ts';
import AccountProviderInterface from '@/composables/accounts/providers/accountProviderInterface.ts';
import { usePostHog } from '@/composables/usePostHog.ts';
import { type Account, ACCOUNT_PROVIDER_ID, type AccountInfo, type ConnectedAccount } from '@/composables/accounts/providers/types.ts';

// Lazy load Reown to reduce initial bundle size (607 KiB)
let reownProviderInstance: AccountProviderInterface | null = null;
let reownProviderInitialized = false;

const ACCOUNT_PROVIDERS: Record<ACCOUNT_PROVIDER_ID, AccountProviderInterface | null> = {
  [ACCOUNT_PROVIDER_ID.PLAY_ACCOUNT_PROVIDER]: new PlayAccountProvider(),
  [ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER]: null, // Lazy loaded
};

async function getReownProvider(): Promise<AccountProviderInterface> {
  if (!reownProviderInstance) {
    const { default: ReownEvmAccountProvider } = await import('@/composables/accounts/providers/reownEvmAccountProvider.ts');
    reownProviderInstance = new ReownEvmAccountProvider();
    ACCOUNT_PROVIDERS[ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER] = reownProviderInstance;
  }

  // Initialize provider if not already initialized
  if (!reownProviderInitialized && isInitialized) {
    await reownProviderInstance.init(onAccountChangeHandler);
    reownProviderInitialized = true;
  }

  return reownProviderInstance;
}

/**
 * Check if user has an existing Reown session (for auto-reconnect)
 * Reown AppKit stores session data in localStorage with @appkit/ prefix
 */
function hasReownSession(): boolean {
  try {
    // Check for AppKit connection state - these keys indicate an active wallet connection
    const connectionStatus = localStorage.getItem('@appkit/connection_status');
    const connectedConnectorId = localStorage.getItem('@appkit/eip155:connected_connector_id');
    const connections = localStorage.getItem('@appkit/connections');

    // If any of these exist with non-empty values, we have a session
    if (
      (connectionStatus && connectionStatus !== '""' && connectionStatus !== 'null') ||
      (connectedConnectorId && connectedConnectorId !== '""' && connectedConnectorId !== 'null') ||
      (connections && connections !== '""' && connections !== 'null' && connections !== '[]')
    ) {
      return true;
    }
  } catch {
    // localStorage not available (private browsing, etc.)
  }
  return false;
}

// Initialize state
let isInitialized = false;
const selectedAccountId = ref<string | null>(null);
const accounts = ref<Record<string, ConnectedAccount>>({});
// Check for Reown session immediately to prevent button flicker on page load
const isReconnecting = ref(hasReownSession());

const selectedAccount = computed<Account | null>(() => {
  if (selectedAccountId.value && accounts.value[selectedAccountId.value]) {
    return accounts.value[selectedAccountId.value]?.account || null;
  }

  return null;
});

const isConnected = computed(() => Object.values(accounts.value).some((acc) => acc.account.id === selectedAccountId.value));

async function addConnectedAccount(accountInfo: AccountInfo, providerId: ACCOUNT_PROVIDER_ID, isPlayAccount = false) {
  const lowerCaseAddress = accountInfo.address.toLowerCase();

  const account: Account = {
    id: accountInfo.id,
    address: lowerCaseAddress,
    name: accountInfo.name,
    isPlayAccount,
    providerId,
  };

  accounts.value[account.id] = {
    account,
    providerId,
  };
}

async function connectAccount(providerId: ACCOUNT_PROVIDER_ID) {
  const provider = providerId === ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER ? await getReownProvider() : ACCOUNT_PROVIDERS[providerId];
  if (!provider) throw new Error(`Provider ${providerId} not initialized`);
  await provider.connect();
}

async function onAccountChangeHandler(accountInfo: AccountInfo, providerId: ACCOUNT_PROVIDER_ID) {
  if (!accounts.value[accountInfo.id]) {
    await addConnectedAccount(accountInfo, providerId);
  }

  selectedAccountId.value = accountInfo.id;

  // Stop showing reconnecting state when Reown account is connected
  if (providerId === ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
    isReconnecting.value = false;

    // Identify user in PostHog when we have a real wallet
    try {
      const { posthog } = usePostHog();

      const lowerCaseAddress = accountInfo.address.toLowerCase();

      posthog.identify(lowerCaseAddress, {
        wallet_address: lowerCaseAddress,
        account_provider: 'reown',
      });

      posthog.capture('connected_reown_wallet', {
        account_id: lowerCaseAddress,
      });
    } catch (err) {
      logWarn('useAccounts', 'PostHog identify failed', err);
    }
  }
}

async function disconnectAccount(accountId: string) {
  const account = accounts.value[accountId];
  if (!account) {
    logWarn('useAccounts', `account with ID ${accountId} not found`);
    return;
  }

  const provider =
    account.providerId === ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER
      ? await getReownProvider()
      : ACCOUNT_PROVIDERS[account.providerId];
  if (provider) {
    provider.disconnect();
  }

  // Aggressively clear all Reown/AppKit localStorage to prevent stale session detection
  if (account.providerId === ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('@appkit/') || key.startsWith('@w3m/') || key.startsWith('wc@2:'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (err) {
      logWarn('useAccounts', 'Failed to clear Reown localStorage', err);
    }
  }

  delete accounts.value[accountId];

  if (selectedAccountId.value === accountId) {
    // Switch back to play account if it exists
    selectedAccountId.value = accounts.value[PLAY_ACCOUNT_ID] ? PLAY_ACCOUNT_ID : null;
  }
}

async function switchConnectedAccount(accountId: string) {
  if (accounts.value[accountId]) {
    selectedAccountId.value = accountId;
  } else {
    logWarn('useAccounts', `account with ID ${accountId} not found`);
  }
}

async function init() {
  if (isInitialized) {
    return;
  }

  // Check for Reown session first to prevent play account flicker
  const hasSession = hasReownSession();

  if (hasSession) {
    isReconnecting.value = true; // Prevent showing play account during reconnect
  }

  // Always add the play account
  const playAccountProvider = ACCOUNT_PROVIDERS[ACCOUNT_PROVIDER_ID.PLAY_ACCOUNT_PROVIDER];
  if (!playAccountProvider) throw new Error('Play account provider not initialized');
  const playAccount = await playAccountProvider.getAccountInfo(PLAY_ACCOUNT_ID);
  await addConnectedAccount(playAccount, ACCOUNT_PROVIDER_ID.PLAY_ACCOUNT_PROVIDER, true);

  // Only select play account if no Reown session exists
  if (!hasSession) {
    selectedAccountId.value = PLAY_ACCOUNT_ID;
    isReconnecting.value = false; // Ensure we show buttons when no wallet connected
  }

  // Initialize Play account provider
  await playAccountProvider.init(onAccountChangeHandler);

  // Defer Reown auto-reconnect to keep 607 KiB bundle off critical path initially
  // Use requestIdleCallback for quick reconnect when browser is idle (usually ~100ms)
  if (hasSession) {
    const reconnect = async () => {
      try {
        await getReownProvider();
        // isReconnecting will be set to false in onAccountChangeHandler when connected
      } catch (err) {
        logWarn('useAccounts', 'failed to auto-reconnect Reown session', err);
        // Reconnection failed, fall back to play account
        selectedAccountId.value = PLAY_ACCOUNT_ID;
        isReconnecting.value = false; // Show UI after failed reconnect
      }
    };

    // Add a failsafe timeout - if reconnection takes more than 2 seconds, likely failed
    const failsafe = setTimeout(() => {
      if (isReconnecting.value && !selectedAccountId.value) {
        selectedAccountId.value = PLAY_ACCOUNT_ID;
        isReconnecting.value = false;
      }
    }, 2000);

    // Clear failsafe if reconnection completes
    const originalValue = isReconnecting.value;
    const checkComplete = setInterval(() => {
      if (!isReconnecting.value && originalValue) {
        clearTimeout(failsafe);
        clearInterval(checkComplete);
      }
    }, 100);

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => void reconnect(), { timeout: 100 });
    } else {
      setTimeout(() => void reconnect(), 50);
    }
  }

  isInitialized = true;
}

function isConnectedToAccountProvider(providerId: ACCOUNT_PROVIDER_ID): boolean {
  return Object.values(accounts.value).some(({ providerId: pid }) => pid === providerId);
}

async function getProviderById(providerId: ACCOUNT_PROVIDER_ID): Promise<AccountProviderInterface | null> {
  if (providerId === ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
    return await getReownProvider();
  }
  return ACCOUNT_PROVIDERS[providerId] || null;
}

function useAccounts() {
  init();

  return {
    accounts,
    selectedAccount,
    selectedAccountId,
    isConnected,
    isReconnecting, // Expose reconnecting state
    addConnectedAccount,
    disconnectAccount,
    switchConnectedAccount,
    connectAccount,
    isConnectedToAccountProvider,
    getProviderById,
  };
}

export default useAccounts;
